import RouterPathProvider from "@core/RouterPath/RouterPathProvider";
import { extractCatalogName } from "@core-ui/utils/extractCatalogName";

// Patterns: "key=value" sticks only for that value, "key=*" sticks for any value
export const STICKY_QUERY_PARAMS = ["view=*", "mode=markdown", "diff=1", "oldScope=*"] as const;

// Diff params point into the current catalog's repository and must not follow a link into another catalog
const CATALOG_SCOPED_PARAMS = ["diff", "oldScope"];

const matchesPattern = (pattern: string, key: string, value: string): boolean => {
	const eqIndex = pattern.indexOf("=");
	if (eqIndex === -1) return false;

	const patKey = pattern.slice(0, eqIndex);
	const patVal = pattern.slice(eqIndex + 1);

	if (patKey !== key) return false;
	return patVal === "*" || patVal === value;
};

// The catalog a path or url points into, ignoring its query
export const catalogOfPath = (pathname?: string): string | undefined =>
	RouterPathProvider.parsePath((pathname ?? "").split("?", 1)[0])?.catalogName;

// currentCatalogName: the catalog of the page the link is rendered on
// search: the query to read instead of window.location, for renders where there is no window
export const getStickyParams = (
	targetPathname: string,
	currentCatalogName?: string,
	search?: string,
): Record<string, string> => {
	const targetCatalogName = RouterPathProvider.parsePath(targetPathname)?.catalogName;
	if (!targetCatalogName) return {};
	if (search === undefined && typeof window === "undefined") return {};

	// A link inside a scoped catalog carries "catalog:commit-<oid>"
	const isSameCatalog = extractCatalogName(targetCatalogName) === extractCatalogName(currentCatalogName);

	const searchParams = new URLSearchParams(search ?? window.location.search);
	const result: Record<string, string> = {};

	for (const [key, value] of searchParams.entries()) {
		if (!isSameCatalog && CATALOG_SCOPED_PARAMS.includes(key)) continue;
		if (STICKY_QUERY_PARAMS.some((pattern) => matchesPattern(pattern, key, value))) {
			result[key] = value;
		}
	}
	// A revision's oldScope is its parent; without `scope` it would compare another article with that old commit.
	if (searchParams.has("scope")) delete result.oldScope;
	return result;
};
